import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, FileText, Trash2, Upload } from "lucide-react";
import { Button, Input, Label } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import {
  brandExemplarApi,
  type ExemplarVersionDto,
} from "@/api/reportsV32";
import { formatDateTimeInTz, useDateFormatter } from "@/utils/datetime";

// LLM & Reports v3.2 §10.3 — "Brand Document Template" section that
// lives on the existing /admin/brand-identity page. Self-contained:
// owns its own queries, mutations, and toasts; the parent page just
// drops it in alongside the other sections (Company info, Visual
// identity, etc.).
//
// Three operations:
//   1. Upload a new exemplar .docx (replaces the active one)
//   2. View the last 10 uploads + reactivate any of them
//   3. Revert to the embedded default template
//
// Old exemplars are NEVER deleted from Blob — finalized reports may
// still be pinned to them via GeneratedReport.ExemplarBlobPathUsed.
// The history list reflects what's still reachable.

const EXEMPLAR_HISTORY_KEY = ["brand-exemplar-history"];

export function BrandTemplateSection() {
  const queryClient = useQueryClient();
  const { tz } = useDateFormatter();
  const { toast } = useToast();

  const history = useQuery<ExemplarVersionDto[]>({
    queryKey: EXEMPLAR_HISTORY_KEY,
    queryFn: () => brandExemplarApi.history(10),
  });

  const active = (history.data ?? []).find((v) => v.isActive) ?? null;

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [versionLabel, setVersionLabel] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  const uploadMutation = useMutation({
    mutationFn: ({ file, label }: { file: File; label: string }) =>
      brandExemplarApi.upload(file, label),
    onSuccess: () => {
      toast({ tone: "success", message: "Exemplar template uploaded." });
      setPendingFile(null);
      setVersionLabel("");
      setValidationError(null);
      queryClient.invalidateQueries({ queryKey: EXEMPLAR_HISTORY_KEY });
    },
    onError: (err: Error) => {
      toast({ tone: "error", message: `Upload failed: ${err.message}` });
    },
  });

  const activateMutation = useMutation({
    mutationFn: (blobPath: string) => brandExemplarApi.activate(blobPath),
    onSuccess: () => {
      toast({ tone: "success", message: "Exemplar version activated." });
      queryClient.invalidateQueries({ queryKey: EXEMPLAR_HISTORY_KEY });
    },
    onError: (err: Error) => {
      toast({ tone: "error", message: `Activate failed: ${err.message}` });
    },
  });

  const revertMutation = useMutation({
    mutationFn: () => brandExemplarApi.revertToDefault(),
    onSuccess: () => {
      toast({ tone: "success", message: "Reverted to the embedded default exemplar." });
      queryClient.invalidateQueries({ queryKey: EXEMPLAR_HISTORY_KEY });
    },
    onError: (err: Error) => {
      toast({ tone: "error", message: `Revert failed: ${err.message}` });
    },
  });

  const handleFilePick = (file: File | null) => {
    setValidationError(null);
    if (!file) {
      setPendingFile(null);
      return;
    }
    if (!file.name.toLowerCase().endsWith(".docx")) {
      setValidationError("Pick a .docx file. The exemplar must be a Word document.");
      setPendingFile(null);
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      setValidationError("File is over 25 MB. Trim images or simplify the template.");
      setPendingFile(null);
      return;
    }
    setPendingFile(file);
  };

  const handleSubmit = () => {
    if (!pendingFile) {
      setValidationError("Pick a file before uploading.");
      return;
    }
    if (!versionLabel.trim()) {
      setValidationError("Add a version label so future admins can disambiguate uploads.");
      return;
    }
    uploadMutation.mutate({ file: pendingFile, label: versionLabel.trim() });
  };

  const handleDownloadActive = async () => {
    try {
      const blob = await brandExemplarApi.download();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = active?.fileName ?? "phoenix-capital-exemplar.docx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        tone: "error",
        message: `Download failed: ${err instanceof Error ? err.message : "Try again."}`,
      });
    }
  };

  return (
    <section className="rounded-md border border-border-light bg-white-pure p-space-4">
      <div className="flex items-center gap-space-2 mb-space-3">
        <span className="text-gold-shadow">
          <FileText size={18} aria-hidden />
        </span>
        <h2 className="font-display font-bold text-h4 text-onyx m-0">
          Brand document template
        </h2>
      </div>
      <p className="text-small text-gray-mid mb-space-4">
        Upload a recent finished document to use as the visual template for all
        generated reports. The template's styles, header, footer, fonts, and
        colors flow into every generated <code className="font-mono">.docx</code> automatically.
      </p>

      {/* Active exemplar summary */}
      <div className="mb-space-4 rounded-md border border-border-light bg-white-warm p-space-3">
        <div className="flex items-start justify-between gap-space-3">
          <div className="min-w-0">
            <p className="font-body text-caption text-gray-mid uppercase tracking-[0.08em]">
              Active exemplar
            </p>
            <p className="font-body text-body text-onyx font-semibold truncate">
              {active ? active.fileName : "Phoenix Capital default template (built-in)"}
            </p>
            <p className="font-body text-caption text-gray-mid mt-space-1">
              {active
                ? `Version ${active.versionLabel} — uploaded ${formatDate(active.uploadedDate, tz)}` +
                  (active.uploadedByDisplayName
                    ? ` by ${active.uploadedByDisplayName}`
                    : "")
                : "No admin upload yet. Reports use the embedded default."}
            </p>
          </div>
          <div className="flex items-center gap-space-2 shrink-0">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleDownloadActive}
            >
              <Download size={14} aria-hidden /> Download
            </Button>
            {active && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => revertMutation.mutate()}
                disabled={revertMutation.isPending}
              >
                <Trash2 size={14} aria-hidden /> Revert to default
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Upload form */}
      <div className="mb-space-4 grid gap-space-3 md:grid-cols-[1fr_auto_auto] items-end">
        <div>
          <Label htmlFor="exemplar-file">Replace template</Label>
          <input
            id="exemplar-file"
            type="file"
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="block w-full text-caption text-onyx file:mr-space-2 file:rounded-md file:border-0 file:bg-gold file:px-space-3 file:py-space-2 file:font-display file:text-caption file:font-bold file:uppercase hover:file:bg-gold-highlight"
            onChange={(e) => handleFilePick(e.target.files?.[0] ?? null)}
          />
          {pendingFile && (
            <p className="font-body text-caption text-gray-mid mt-space-1 truncate">
              Ready to upload: {pendingFile.name} (
              {(pendingFile.size / (1024 * 1024)).toFixed(1)} MB)
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="exemplar-version">Version label</Label>
          <Input
            id="exemplar-version"
            value={versionLabel}
            onChange={(e) => setVersionLabel(e.target.value)}
            placeholder="e.g. v1.0 - 2026-04"
            maxLength={50}
          />
        </div>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={!pendingFile || !versionLabel.trim() || uploadMutation.isPending}
        >
          <Upload size={14} aria-hidden />
          {uploadMutation.isPending ? "Uploading…" : "Replace"}
        </Button>
      </div>
      {validationError && (
        <p className="-mt-space-2 mb-space-3 text-caption text-status-error" role="alert">
          {validationError}
        </p>
      )}

      <p className="font-body text-caption text-gray-mid mb-space-3">
        Tip: for the header's right-side text to show the report title
        automatically, use a Word <code className="font-mono">{`{ TITLE }`}</code>{" "}
        field code instead of literal text. Insert &gt; Quick Parts &gt;
        Field &gt; Title in Word.
      </p>

      {/* History */}
      <div>
        <h3 className="font-display text-h4 text-onyx mb-space-2">
          Version history (last 10)
        </h3>
        {history.isLoading && (
          <p className="font-body text-caption text-gray-mid">Loading history…</p>
        )}
        {history.isError && (
          <p className="font-body text-caption text-status-error">
            Could not load history.
          </p>
        )}
        {history.data && history.data.length === 0 && (
          <p className="font-body text-caption text-gray-mid">
            No admin-uploaded exemplars yet.
          </p>
        )}
        {history.data && history.data.length > 0 && (
          <ul className="divide-y divide-border-light rounded-md border border-border-light">
            {history.data.map((row) => (
              <li
                key={row.blobPath}
                className="flex items-center justify-between gap-space-3 px-space-3 py-space-2"
              >
                <div className="min-w-0">
                  <p className="font-body text-body text-onyx truncate">
                    {row.versionLabel}{" "}
                    {row.isActive && (
                      <span className="font-display text-caption uppercase tracking-[0.1em] text-gold-shadow ml-space-2">
                        Active
                      </span>
                    )}
                  </p>
                  <p className="font-body text-caption text-gray-mid truncate">
                    {row.fileName} — {formatDate(row.uploadedDate, tz)}
                    {row.uploadedByDisplayName ? ` • ${row.uploadedByDisplayName}` : ""}
                  </p>
                </div>
                <div className="shrink-0">
                  {!row.isActive && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => activateMutation.mutate(row.blobPath)}
                      disabled={activateMutation.isPending}
                    >
                      Activate
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function formatDate(iso: string, tz: string): string {
  return formatDateTimeInTz(iso, tz) || iso;
}
